import { useState, useEffect } from 'react';
import { api } from '../services/api';
import { useTenant } from '../contexts/TenantContext';
import { logger } from '../utils/logger';

const WEEKDAYS = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];

interface PType {
  id: string;
  name: string;
  slug: string;
  active: boolean;
  cutoff_mode: 'RELATIVE' | 'FIXED_WEEKLY';
  cutoff_weekday: number | null;
  cutoff_offset_days: number | null;
  cutoff_hour: number;
  pickup_mode: 'SPECIFIC_DAY' | 'FLEXIBLE_WINDOW';
  pickup_days: number[];
  delivery_days?: number[];
  prep_options: string[];
}

interface FormState {
  name: string;
  slug: string;
  active: boolean;
  cutoff_mode: 'RELATIVE' | 'FIXED_WEEKLY';
  cutoff_weekday: number;
  cutoff_offset_days: number;
  cutoff_hour: number;
  pickup_mode: 'SPECIFIC_DAY' | 'FLEXIBLE_WINDOW';
  pickup_days: number[];
  delivery_days?: number[];
  prep_options: string; // saisie séparée par virgules
}

const emptyForm = (): FormState => ({
  name: '',
  slug: '',
  active: true,
  cutoff_mode: 'RELATIVE',
  cutoff_weekday: 1,
  cutoff_offset_days: 1,
  cutoff_hour: 6,
  pickup_mode: 'SPECIFIC_DAY',
  pickup_days: [5, 6],
  delivery_days: [],
  prep_options: '',
});

const slugify = (s: string) =>
  s.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

function cadenceLabel(t: PType): string {
  if (t.cutoff_mode === 'FIXED_WEEKLY') {
    return `Commande ${WEEKDAYS[t.cutoff_weekday ?? 1]} ${t.cutoff_hour}h`;
  }
  return `Commande J-${t.cutoff_offset_days ?? 1} à ${t.cutoff_hour}h`;
}

export default function PreorderTypesPage() {
  const { tenant } = useTenant();
  const [types, setTypes] = useState<PType[]>([]);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState<string | 'new' | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm());
  const [saving, setSaving] = useState(false);

  const load = async () => {
    if (!tenant?.id) return;
    setLoading(true);
    try {
      const data = await api.get('/preorders/types', { headers: { 'X-Tenant-ID': tenant.id } });
      setTypes(Array.isArray(data) ? data : []);
    } catch (e) {
      logger.error('Erreur chargement types précommande:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenant?.id]);

  const startNew = () => {
    setForm(emptyForm());
    setEditingId('new');
  };

  const startEdit = (t: PType) => {
    setForm({
      name: t.name,
      slug: t.slug,
      active: t.active,
      cutoff_mode: t.cutoff_mode,
      cutoff_weekday: t.cutoff_weekday ?? 1,
      cutoff_offset_days: t.cutoff_offset_days ?? 1,
      cutoff_hour: t.cutoff_hour,
      pickup_mode: t.pickup_mode,
      pickup_days: t.pickup_days || [],
      delivery_days: t.delivery_days || [],
      prep_options: (t.prep_options || []).join(', '),
    });
    setEditingId(t.id);
  };

  const toggleDeliveryDay = (d: number) => {
    setForm((f) => {
      const cur = f.delivery_days || [];
      return {
        ...f,
        delivery_days: cur.includes(d) ? cur.filter((x) => x !== d) : [...cur, d].sort(),
      };
    });
  };

  const togglePickupDay = (d: number) => {
    setForm((f) => ({
      ...f,
      pickup_days: f.pickup_days.includes(d) ? f.pickup_days.filter((x) => x !== d) : [...f.pickup_days, d].sort(),
    }));
  };

  const handleSave = async () => {
    if (!tenant?.id) return;
    if (!form.name.trim()) {
      alert('Le nom est requis');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        slug: form.slug.trim() || slugify(form.name),
        active: form.active,
        cutoff_mode: form.cutoff_mode,
        cutoff_weekday: form.cutoff_mode === 'FIXED_WEEKLY' ? Number(form.cutoff_weekday) : null,
        cutoff_offset_days: form.cutoff_mode === 'RELATIVE' ? Number(form.cutoff_offset_days) : null,
        cutoff_hour: Number(form.cutoff_hour),
        pickup_mode: form.pickup_mode,
        pickup_days: form.pickup_days,
        delivery_days: form.delivery_days || [],
        prep_options: form.prep_options.split(',').map((s) => s.trim()).filter(Boolean),
      };
      const headers = { 'X-Tenant-ID': tenant.id };
      if (editingId === 'new') {
        await api.post('/preorders/types', payload, { headers });
      } else {
        await api.patch(`/preorders/types/${editingId}`, payload, { headers });
      }
      setEditingId(null);
      load();
    } catch (e: any) {
      alert('❌ ' + (e?.response?.data?.message || e?.message || 'Échec de l\'enregistrement'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (t: PType) => {
    if (!tenant?.id) return;
    if (!window.confirm(`Supprimer le type "${t.name}" ?`)) return;
    try {
      await api.delete(`/preorders/types/${t.id}`, { headers: { 'X-Tenant-ID': tenant.id } });
      load();
    } catch (e: any) {
      alert('❌ ' + (e?.response?.data?.message || e?.message || 'Suppression impossible'));
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Types de précommande</h1>
          <p className="mt-1 text-gray-600">
            Chaque type (Pain, Viande, Poisson…) définit sa cadence de commande et ses options.
          </p>
        </div>
        <button
          onClick={startNew}
          className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-medium"
        >
          + Nouveau type
        </button>
      </div>

      {/* Formulaire création / édition */}
      {editingId && (
        <div className="bg-white rounded-lg shadow border border-emerald-200 p-6 mb-6 space-y-4">
          <h2 className="text-lg font-semibold">{editingId === 'new' ? 'Nouveau type' : 'Modifier le type'}</h2>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nom</label>
              <input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full border border-gray-300 rounded-md px-3 py-2"
                placeholder="Ex. Traiteur"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Slug (auto si vide)</label>
              <input
                value={form.slug}
                onChange={(e) => setForm({ ...form, slug: e.target.value })}
                className="w-full border border-gray-300 rounded-md px-3 py-2"
                placeholder={slugify(form.name) || 'traiteur'}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Mode d'échéance</label>
              <select
                value={form.cutoff_mode}
                onChange={(e) => setForm({ ...form, cutoff_mode: e.target.value as FormState['cutoff_mode'] })}
                className="w-full border border-gray-300 rounded-md px-3 py-2"
              >
                <option value="RELATIVE">Relatif au retrait (ex. pain, la veille)</option>
                <option value="FIXED_WEEKLY">Jour fixe hebdomadaire (ex. viande, lundi)</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Heure limite</label>
              <input
                type="number"
                min={0}
                max={23}
                value={form.cutoff_hour}
                onChange={(e) => setForm({ ...form, cutoff_hour: Number(e.target.value) })}
                className="w-full border border-gray-300 rounded-md px-3 py-2"
              />
            </div>
          </div>

          {form.cutoff_mode === 'FIXED_WEEKLY' ? (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Jour de commande au producteur</label>
              <select
                value={form.cutoff_weekday}
                onChange={(e) => setForm({ ...form, cutoff_weekday: Number(e.target.value) })}
                className="w-full border border-gray-300 rounded-md px-3 py-2"
              >
                {WEEKDAYS.map((w, i) => (
                  <option key={i} value={i}>{w}</option>
                ))}
              </select>
            </div>
          ) : (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nb de jours avant la fabrication</label>
              <input
                type="number"
                min={0}
                value={form.cutoff_offset_days}
                onChange={(e) => setForm({ ...form, cutoff_offset_days: Number(e.target.value) })}
                className="w-full border border-gray-300 rounded-md px-3 py-2"
              />
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Mode de reprise</label>
            <select
              value={form.pickup_mode}
              onChange={(e) => setForm({ ...form, pickup_mode: e.target.value as FormState['pickup_mode'] })}
              className="w-full border border-gray-300 rounded-md px-3 py-2"
            >
              <option value="SPECIFIC_DAY">Jour précis choisi par le client (pain)</option>
              <option value="FLEXIBLE_WINDOW">Fenêtre flexible (viande/poisson, à convenance)</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Jours de reprise autorisés</label>
            <div className="flex gap-2">
              {WEEKDAYS.map((w, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => togglePickupDay(i)}
                  className={`px-3 py-2 rounded-md text-sm border-2 ${
                    form.pickup_days.includes(i)
                      ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                      : 'border-gray-200 text-gray-500'
                  }`}
                >
                  {w}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Jours de livraison par le fournisseur
            </label>
            <div className="flex gap-2">
              {WEEKDAYS.map((w, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => toggleDeliveryDay(i)}
                  className={`px-3 py-2 rounded-md text-sm border-2 ${
                    (form.delivery_days || []).includes(i)
                      ? 'border-amber-500 bg-amber-50 text-amber-700'
                      : 'border-gray-200 text-gray-500'
                  }`}
                >
                  {w}
                </button>
              ))}
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Les jours où ce fournisseur vous livre. La commande est calée sur le
              dernier de ces jours avant la reprise — c'est ce qui permet de commander
              un pain cuit le vendredi pour un retrait le samedi. Laissez vide pour
              conserver le comportement actuel.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Options de préparation (séparées par des virgules)
            </label>
            <input
              value={form.prep_options}
              onChange={(e) => setForm({ ...form, prep_options: e.target.value })}
              className="w-full border border-gray-300 rounded-md px-3 py-2"
              placeholder="Ex. coupé   (laisser vide pour viande/poisson en colis)"
            />
          </div>

          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} />
            Type actif
          </label>

          <div className="flex gap-3 pt-2">
            <button
              onClick={() => setEditingId(null)}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700"
            >
              Annuler
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-6 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white rounded-lg font-medium"
            >
              {saving ? 'Enregistrement…' : 'Enregistrer'}
            </button>
          </div>
        </div>
      )}

      {/* Liste des types */}
      {loading ? (
        <div className="p-8 text-center text-gray-500">Chargement…</div>
      ) : types.length === 0 ? (
        <div className="p-8 text-center text-gray-500 bg-white rounded-lg shadow">Aucun type. Créez-en un.</div>
      ) : (
        <div className="bg-white rounded-lg shadow divide-y">
          {types.map((t) => (
            <div key={t.id} className="p-4 flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-gray-900">{t.name}</span>
                  {!t.active && <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded">inactif</span>}
                </div>
                <div className="text-sm text-gray-500 mt-1">
                  {cadenceLabel(t)} · Reprise : {(t.pickup_days || []).map((d) => WEEKDAYS[d]).join('/')}
                  {(t.prep_options || []).length > 0 && ` · Options : ${t.prep_options.join(', ')}`}
                </div>
              </div>
              <div className="flex gap-2">
                <button onClick={() => startEdit(t)} className="px-3 py-1.5 text-sm border border-gray-300 rounded-md">
                  Modifier
                </button>
                <button
                  onClick={() => handleDelete(t)}
                  className="px-3 py-1.5 text-sm bg-red-50 text-red-700 rounded-md"
                >
                  Supprimer
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
